import * as React from 'react';
import useRemoteLogger from './useRemoteLogger';
import useTrackingHelpers from './useTrackingHelpers';
import useLoginState from './useLoginState';
import useAppshellMeta from './useAppshellMeta';
import useTranslator from 'Client/hooks/useTranslator';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { MicroFrontendAppShellProps } from 'Common/appshell_types';
import HybridLink from 'Client/components/Generic/Links/HybridLink';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { ErrorBoundary } from 'Client/components/Generic/ErrorBoundaries';
import useConfig from 'Client/hooks/useConfig';
import AppBridge from 'Common/helpers/AppBridge';
import routes from 'Common/routes/paths';
import useRouter from 'Client/hooks/useRouter';
import getUseServerSideAction from 'Client/hooks/useFetchingAction/useGetServerSideAction';

function withMicroFrontendProps(WrappedComponent: React.ComponentType<MicroFrontendAppShellProps>, remoteName: string) {
    const MicroFrontend = (customProps: { [key: string]: any }) => {
        const t = useTranslator();
        const config = useConfig();
        const featureToggles = useFeatureToggle();
        const remoteLogger = useRemoteLogger(remoteName);
        const loginState = useLoginState();
        const { host } = useRouter();
        const routingHelpers = {
            host,
            HybridLink,
            useBellaPush: useBellaPush(),
            routes,
        };
        const trackingHelpers = useTrackingHelpers(remoteName);
        const appshellMeta = useAppshellMeta();

        const { useUniversalFetch, state } = getUseServerSideAction(remoteName);

        return (
            <ErrorBoundary
                boundaryName={`${remoteName}-appshell-wrapper`}
                fallbackComponent={null}
                logger={remoteLogger}
            >
                <WrappedComponent
                    {...customProps}
                    t={t}
                    featureToggles={featureToggles}
                    logger={remoteLogger}
                    routingHelpers={routingHelpers}
                    trackingHelpers={trackingHelpers}
                    config={config}
                    AppBridge={AppBridge}
                    loginState={loginState}
                    appshellMeta={appshellMeta}
                    useUniversalFetch={useUniversalFetch}
                    fetchedUniversalState={state}
                />
            </ErrorBoundary>
        );
    };

    MicroFrontend.displayName = `withMicroFrontendProps(${remoteName})`;
    return MicroFrontend;
}

export default withMicroFrontendProps;
